import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, X, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

const iconOptions = [
  'Building', 'Users', 'Wifi', 'Shield', 'Bell', 'Headphones',
  'Coffee', 'Briefcase', 'Clock', 'MapPin', 'Phone', 'Mail'
];

export default function AdminCareCards() {
  const [cards, setCards] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [formData, setFormData] = useState({
    icon: 'Building',
    icon_url: '',
    title: '',
    description: '',
    order: 0
  });

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await apiClient.get('/care-cards');
      setCards(response.data);
    } catch (error) {
      console.error('Error fetching care cards:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        id: editingCard?.id || undefined
      };

      if (editingCard) {
        await apiClient.put(`/admin/care-cards/${editingCard.id}`, payload);
        toast.success('Care card updated successfully!');
      } else {
        await apiClient.post('/admin/care-cards', payload);
        toast.success('Care card created successfully!');
      }

      setIsModalOpen(false);
      resetForm();
      fetchCards();
    } catch (error) {
      toast.error('Failed to save care card');
    }
  };

  const handleEdit = (card) => {
    setEditingCard(card);
    setFormData({
      icon: card.icon,
      icon_url: card.icon_url || '',
      title: card.title,
      description: card.description,
      order: card.order
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this care card?')) {
      try {
        await apiClient.delete(`/admin/care-cards/${id}`);
        toast.success('Care card deleted successfully!');
        fetchCards();
      } catch (error) {
        toast.error('Failed to delete care card');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      icon: 'Building',
      icon_url: '',
      title: '',
      description: '',
      order: 0
    });
    setEditingCard(null);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Manage Care Cards</h1>
            <Button onClick={() => setIsModalOpen(true)} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" />
              Add Card
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card) => (
            <div key={card.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                    {card.icon_url ? (
                      <img src={card.icon_url} alt={card.title} className="w-8 h-8 object-contain" />
                    ) : (
                      <span className="text-sm font-medium">{card.icon}</span>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{card.title}</h3>
                    <span className="text-xs text-gray-500">Order: {card.order}</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">{card.description}</p>
              <div className="flex space-x-2">
                <Button onClick={() => handleEdit(card)} size="sm" variant="outline">
                  <Edit size={16} className="mr-1" />
                  Edit
                </Button>
                <Button onClick={() => handleDelete(card.id)} size="sm" variant="destructive">
                  <Trash2 size={16} className="mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCard ? 'Edit Care Card' : 'Add New Care Card'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Custom Icon Upload (PNG/SVG)</Label>
              <p className="text-sm text-gray-500 mb-2">Upload a custom icon image. If uploaded, this will be used instead of the selected icon below.</p>
              <ImageUpload
                value={formData.icon_url}
                onChange={(url) => setFormData({ ...formData, icon_url: url })}
                label="Icon Image"
              />
            </div>
            <div>
              <Label>Fallback Icon (Lucide)</Label>
              <p className="text-sm text-gray-500 mb-2">Used if no custom icon is uploaded.</p>
              <Select value={formData.icon} onValueChange={(value) => setFormData({ ...formData, icon: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select icon" />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map((icon) => (
                    <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Executive Cabins"
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the service"
                required
                rows={3}
              />
            </div>
            <div>
              <Label>Order</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                Save Card
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
